import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Box } from '@material-ui/core';
import { useStyles } from './OrderHistoryPage.styles';
import { LeftMenuDrawer } from '../../navigation/LeftMenuDrawer/LeftMenuDrawer';
import { SearchMenuBar } from '../../navigation/SearchMenuBar/SearchMenuBar';
import { DesktopLayout } from '../../layout/DesktopLayout/DesktopLayout';
import ActiveDriversMap from '../../layout/ActiveDriversMap/ActiveDriversMap';
import {
  useActiveDrivers,
  useActiveDriversRequest,
} from '../../../hooks/useActiveDrivers/useActiveDrivers';
import useIsDesktop from '../../../hooks/useIsDesktop/useIsDesktop';
import { DriverSortedList } from './components/DriverSortedList';
import SortFilter from './components/SortFilter';
import { DeliverySortedList } from './components/DeliverySortedList';
import { historyDateRanges } from '../../../utils/DateUtils/historyRange';
import OrderHistoryFooter from '../../layout/OrderHistoryFooter';
import { useHistoryTraversal } from './OrderHistoryPage.hooks';
import { usePortalAccess } from '../../../hooks/usePortalAccess';
import { useMapContext } from '../../../context/MapContext';

export const getDriversTitleBySortBy = (sortBy) =>
  sortBy === 'driver' ? 'Drivers' : 'Orders';

function HistoryMap() {
  const request = useActiveDriversRequest();
  const { filterOptions, onFilterChange, filters, driverMarkers } = useActiveDrivers(request);
  const { updateMarkers } = useMapContext();
  useEffect(() => {
    updateMarkers(driverMarkers, { centerAfterUpdate: true, shouldZoom: false });
  }, [driverMarkers, updateMarkers]);
  return <ActiveDriversMap filterOptions={filterOptions} onFilterChange={onFilterChange} filters={filters} />;
}

export function OrderHistoryPage() {
  const classes = useStyles();

  const isDesktop = useIsDesktop();
  const { dispatcher } = usePortalAccess();

  const [metadata, setMetadata] = useState({});

  const [sortBy, setSortBy] = useState('driver');
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    return historyDateRanges(now).today;
  });

  const traversal = useHistoryTraversal(dateRange);
  const validMetadata = metadata?.rangeKey === JSON.stringify(dateRange) && metadata?.sortBy === sortBy;
  const list = useMemo(
    () =>
      ({
        driver: (
          <DriverSortedList
            key={`drivers-${JSON.stringify(dateRange)}`}
            type="order-history"
            setMetadata={setMetadata}
            dateRange={dateRange}
            traversal={traversal}
          />
        ),
        date: (
          <DeliverySortedList key={`orders-${JSON.stringify(dateRange)}`} setMetadata={setMetadata} dateRange={dateRange} />
        ),
      }[sortBy]),
    [sortBy, dateRange, traversal]
  );

  const driversTitle = getDriversTitleBySortBy(sortBy);

  return isDesktop ? (
    <main className={clsx(classes.content)}>
      <LeftMenuDrawer />
      <DesktopLayout
        HeaderComponent={
          <Box className={classes.desktopSearchBar}>
            <SearchMenuBar title="Order History" />
          </Box>
        }
        MapComponent={
          dispatcher ? <HistoryMap /> : null
        }
        FooterComponent={
          <Box className={classes.desktopFooterSummary}>
            <OrderHistoryFooter
              driversTitle={driversTitle}
              drivers={metadata?.rangeKey === JSON.stringify(dateRange) && metadata?.sortBy === sortBy ? metadata.totalResults : null}
              earned={validMetadata ? metadata.totalEarned : null}
              earningsStatus={validMetadata ? metadata.earningsStatus : undefined}
              completedOrders={validMetadata ? metadata.completedOrders : null}
              missingCompensationCount={validMetadata ? metadata.missingCompensationCount : null}
            />
          </Box>
        }
        paddingBottom={100}
      >
        <Box padding={1}>
          <SortFilter
            sortBy={sortBy}
            setSortBy={setSortBy}
            setDateRange={setDateRange}
            dateRange={dateRange}
          />
          {list}
        </Box>
      </DesktopLayout>
    </main>
  ) : (
    <main className={clsx(classes.content)}>
      <LeftMenuDrawer />
      <Box className={classes.header}>
        <SearchMenuBar title="Order History" />
      </Box>
      <Box className={classes.body}>
        <SortFilter
          sortBy={sortBy}
          setSortBy={setSortBy}
          setDateRange={setDateRange}
          dateRange={dateRange}
        />
        {list}
      </Box>
      <Box className={classes.footer}>
        <OrderHistoryFooter
          driversTitle={driversTitle}
          drivers={metadata?.rangeKey === JSON.stringify(dateRange) && metadata?.sortBy === sortBy ? metadata.totalResults : null}
          earned={validMetadata ? metadata.totalEarned : null}
          earningsStatus={validMetadata ? metadata.earningsStatus : undefined}
          completedOrders={validMetadata ? metadata.completedOrders : null}
          missingCompensationCount={validMetadata ? metadata.missingCompensationCount : null}
        />
      </Box>
    </main>
  );
}
